import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts";
import ThemedChartTooltip, { ThemedTooltipBox } from "../../shared/ThemedChartTooltip";
import InfoTip from "../../shared/InfoTip";
import { BRAND_BLUE } from "../../shared/colors";
import { fmtCurrency } from "../../shared/format.js";

/**
 * WindowedPerformance — performance_windows evidence component.
 *
 * Same three-figure layout as ExpectedVsActual but with a local
 * segmented control to switch between 30, 90, and 365 day windows.
 * No refetch — all three are in the payload.
 *
 * Caption: "Conversion rates are learned over the trailing 180 days
 * in every view; only the measured period changes."
 *
 * Spec references: §7 (performance_windows), §0f.3 (metric window).
 */

const fmtNum = (v) => Number(v ?? 0).toLocaleString();

const WINDOW_OPTIONS = [30, 90, 365];

const WindowedPerformance = ({ panel }) => {
  const f = panel.facts || {};
  const windows = f.windows || [];
  const defaultWindow = f.default_window || 30;

  const [selectedDays, setSelectedDays] = useState(defaultWindow);

  // Find the matching window (fall back to first)
  const current =
    windows.find((w) => w.days === selectedDays) || windows[0];

  if (!current) return null;

  const expectedIgnoring = Number(current.expected_ignoring_weather) || 0;
  const expectedGiven = Number(current.expected_given_weather) || 0;
  const actual = Number(current.actual_bookings) || 0;
  const perfPct =
    Number(current.performance_vs_weather_expectation_pct) || 0;

  const isPositive = perfPct >= 0;
  const perfColor = isPositive
    ? "text-emerald-600 dark:text-emerald-400"
    : "text-red-500";

  const chartData = [
    { label: "Expected\nignoring weather", value: expectedIgnoring, color: "#9CA3AF" },
    { label: "Expected\ngiven weather", value: expectedGiven, color: "#60A5FA" },
    { label: "Actual", value: actual, color: BRAND_BLUE },
  ];

  return (
    <div className="space-y-4">
      {/* ─── Segmented control + Hero number ──────────────────────── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-baseline gap-2">
          <span className={`text-4xl font-extrabold leading-none ${perfColor}`}>
            {isPositive ? "+" : ""}
            {perfPct}%
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            vs weather expectation
          </span>
        </div>
        <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {WINDOW_OPTIONS.map((days) => {
            const active = selectedDays === days;
            return (
              <button
                key={days}
                type="button"
                onClick={() => setSelectedDays(days)}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  active
                    ? "bg-[#32B1FF] text-white"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                } ${days !== 365 ? "border-r border-gray-200 dark:border-gray-700" : ""}`}
              >
                Last {days} days
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Three-bar chart (in a card) ──────────────────────────── */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <span>Bookings: Expected vs Actual</span>
            <InfoTip text="Compares what you'd expect ignoring weather, what the weather predicts, and what actually happened. The gap between the first two bars is the weather effect; the gap between the last two is your performance." />
          </h3>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={{ stroke: "#374151" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              width={45}
              allowDecimals={false}
            />
            <ThemedChartTooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <ThemedTooltipBox title={label.replace(/\n/g, " ")}>
                    <div>Bookings: {fmtNum(payload[0].value)}</div>
                  </ThemedTooltipBox>
                );
              }}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={48}>
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* ─── Caption ───────────────────────────────────────────────── */}
        <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center italic mt-3">
          Conversion rates are learned over the trailing 180 days in every view; only the measured period changes.
        </p>
      </div>

      {/* ─── Revenue lost for this window ──────────────────────────── */}
      {current.revenue_lost_to_conditions != null && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Estimated revenue lost to conditions:{" "}
          <span className="font-semibold">
            {fmtCurrency(current.revenue_lost_to_conditions)}
          </span>
        </p>
      )}
    </div>
  );
};

export default WindowedPerformance;
